import React, { useCallback, useMemo, useState } from "react";

import { EDSDatePicker } from "../../../../../components/eds/EDSDatePicker";
import { setTelemetryLabels } from "../../../../../telemetry/telemetry";
import {
  EnquiryStatus,
  FilterOptions,
  FacetCounts
} from "../../../../../types/enquiries";
import { User } from "../../../../../types/VenueGroup";
import {
  formatDateToFilterValue,
  parseFilterValueToDate
} from "../../../../../utils/date";
import {
  STATUS_CONFIG,
  COMPUTED_NOTIFICATIONS,
  STANDARD_NOTIFICATIONS,
  CUSTOMER_DETAILS_NOTIFICATION,
  NEW_MESSAGES_NOTIFICATION,
  CUSTOMER_CANCELLED_NOTIFICATION,
  AUTO_CONFIRM_FILTER_VALUES
} from "../constants";

interface NotificationItemProps {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}

const NotificationItem = React.memo<NotificationItemProps>(
  ({ label, count, isActive, onClick }) => (
    <button
      aria-pressed={isActive}
      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm mb-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        isActive
          ? "bg-accent text-accent-foreground font-medium"
          : "hover:bg-accent/50"
      }`}
      onClick={onClick}
    >
      <span>{label}</span>
      <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">
        {count}
      </span>
    </button>
  )
);

type FilterStrategy = (
  isActive: boolean,
  updated: string[]
) => Partial<FilterOptions>;

const FILTER_STRATEGIES: Record<string, FilterStrategy> = {
  [CUSTOMER_CANCELLED_NOTIFICATION.filterValue]: (isActive) => ({
    status: isActive ? undefined : ["cancelled" as EnquiryStatus]
  }),
  [NEW_MESSAGES_NOTIFICATION.filterValue]: (isActive) => ({
    status: isActive
      ? undefined
      : (["new", "in_progress", "complete", "cancelled"] as EnquiryStatus[])
  })
};

const getAutoConfirmStrategy = (
  isActive: boolean,
  updated: string[]
): Partial<FilterOptions> => {
  const hasRemainingAutoConfirm = updated.some((n) =>
    AUTO_CONFIRM_FILTER_VALUES.has(n)
  );
  return {
    status: hasRemainingAutoConfirm
      ? (["new", "in_progress", "complete"] as EnquiryStatus[])
      : undefined
  };
};

export interface EnquiriesSidebarProps {
  filters: FilterOptions;
  facetCounts: FacetCounts | null;
  users: User[];
  currentUserId?: string;
  isInternal: boolean;
  hasBookingCustomFieldLabel: boolean;
  updateFilter: (
    key: keyof FilterOptions,
    value: FilterOptions[keyof FilterOptions]
  ) => void;
  batchUpdateFilters: (updates: Partial<FilterOptions>) => void;
}

export const EnquiriesSidebar: React.FC<EnquiriesSidebarProps> = ({
  filters,
  facetCounts,
  users,
  currentUserId,
  isInternal,
  hasBookingCustomFieldLabel,
  updateFilter,
  batchUpdateFilters
}) => {
  const [userSearchQuery, setUserSearchQuery] = useState("");

  const sortedUsers = useMemo(() => {
    if (!currentUserId) return users;
    return [...users].sort((a, b) => {
      if (a._id === currentUserId) return -1;
      if (b._id === currentUserId) return 1;
      return 0;
    });
  }, [users, currentUserId]);

  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) return sortedUsers;
    const query = userSearchQuery.toLowerCase();
    return sortedUsers.filter((user) =>
      user.full_name.toLowerCase().includes(query)
    );
  }, [sortedUsers, userSearchQuery]);

  const selectedUserIds = useMemo(() => {
    if (!filters.assigned_to) return [];
    if (filters.assigned_to === "me" && currentUserId) return [currentUserId];
    return filters.assigned_to.split(",").filter(Boolean);
  }, [filters.assigned_to, currentUserId]);

  const handleAssignedToSelect = useCallback(
    (userId: string) => {
      const raw = filters.assigned_to ?? "";
      const normalised = raw === "me" && currentUserId ? currentUserId : raw;
      const current = normalised ? normalised.split(",").filter(Boolean) : [];
      const updated = current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId];
      updateFilter(
        "assigned_to",
        updated.length > 0 ? updated.join(",") : undefined
      );
    },
    [filters.assigned_to, currentUserId, updateFilter]
  );

  const handleClearAssignedTo = useCallback(() => {
    updateFilter("assigned_to", undefined);
    setUserSearchQuery("");
  }, [updateFilter]);

  const handleStatusFilterClick = useCallback(
    (status: EnquiryStatus) => {
      const currentStatuses = filters.status ?? [];
      const newStatuses = currentStatuses.includes(status)
        ? currentStatuses.filter((s) => s !== status)
        : [...currentStatuses, status];
      updateFilter("status", newStatuses.length > 0 ? newStatuses : undefined);
    },
    [filters.status, updateFilter]
  );

  const handleDateFromSelect = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        updateFilter("date_from", undefined);
        return;
      }
      const dateString = formatDateToFilterValue(date);

      // If date_to is set and is before the new date_from, batch-clear it
      if (filters.date_to && filters.date_to < dateString) {
        batchUpdateFilters({ date_from: dateString, date_to: undefined });
      } else {
        updateFilter("date_from", dateString);
      }
    },
    [filters.date_to, updateFilter, batchUpdateFilters]
  );

  const handleDateToSelect = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        updateFilter("date_to", undefined);
        return;
      }
      const dateString = formatDateToFilterValue(date);

      // If date_from is set and is after the new date_to, batch-clear it
      if (filters.date_from && filters.date_from > dateString) {
        batchUpdateFilters({ date_to: dateString, date_from: undefined });
      } else {
        updateFilter("date_to", dateString);
      }
    },
    [filters.date_from, updateFilter, batchUpdateFilters]
  );

  const handleClearDateRange = useCallback(() => {
    batchUpdateFilters({ date_from: undefined, date_to: undefined });
  }, [batchUpdateFilters]);

  const dateFrom = filters.date_from
    ? parseFilterValueToDate(filters.date_from)
    : undefined;
  const dateTo = filters.date_to
    ? parseFilterValueToDate(filters.date_to)
    : undefined;

  const handleNotificationFilterClick = useCallback(
    (filterValue: string) => {
      const current = filters.notifications ?? [];
      const isActive = current.includes(filterValue);
      const updated = isActive
        ? current.filter((n) => n !== filterValue)
        : [...current, filterValue];
      const nextNotifications = updated.length > 0 ? updated : undefined;

      setTelemetryLabels({
        "NotificationCentre-Filter": filterValue,
        "NotificationCentre-Action": isActive ? "deselect" : "select"
      });

      const strategy =
        FILTER_STRATEGIES[filterValue] ??
        (AUTO_CONFIRM_FILTER_VALUES.has(filterValue)
          ? getAutoConfirmStrategy
          : undefined);

      if (strategy) {
        batchUpdateFilters({
          notifications: nextNotifications,
          ...strategy(isActive, updated)
        });
        return;
      }

      updateFilter("notifications", nextNotifications);
    },
    [filters.notifications, updateFilter, batchUpdateFilters]
  );

  const hasDateFilter = Boolean(filters.date_from ?? filters.date_to);

  return (
    <div className="flex flex-col h-full max-h-[calc(100dvh-60px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))] overflow-y-auto pb-6">
      <div className="mx-4 mt-4 mb-4">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Status
        </h3>
        {(
          [
            "new",
            "in_progress",
            "complete",
            "cancelled",
            "lost",
            "deleted"
          ] as EnquiryStatus[]
        ).map((status) => (
          <button
            key={status}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm mb-1 transition-colors ${
              filters.status?.includes(status)
                ? "bg-accent text-accent-foreground font-medium"
                : "hover:bg-accent/50"
            }`}
            onClick={() => handleStatusFilterClick(status)}
          >
            <span>{STATUS_CONFIG[status].label}</span>
            <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">
              {facetCounts?.status[status] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Date Range
          </h3>
          {hasDateFilter && (
            <button
              className="text-xs text-muted-foreground hover:text-foreground transition-colors min-h-6 min-w-6 p-1"
              onClick={handleClearDateRange}
              aria-label="Clear date range filter"
            >
              Clear
            </button>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <EDSDatePicker
            date={dateFrom}
            onSelect={handleDateFromSelect}
            placeholder="From"
            label="From"
            id="enquiries-date-from"
            className="w-full"
          />
          <EDSDatePicker
            date={dateTo}
            onSelect={handleDateToSelect}
            placeholder="To"
            label="To"
            id="enquiries-date-to"
            className="w-full"
          />
        </div>
      </div>

      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Assigned to
          </h3>
          {filters.assigned_to && (
            <button
              className="text-xs text-muted-foreground hover:text-foreground transition-colors min-h-6 min-w-6 p-1"
              onClick={handleClearAssignedTo}
              aria-label="Clear assigned to filter"
            >
              Clear
            </button>
          )}
        </div>
        <div className="mb-2">
          <input
            type="text"
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            placeholder="Search users..."
            aria-label="Search users"
            className="w-full px-3 py-1.5 rounded-md text-sm border border-border bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div
          className="flex flex-col max-h-48 overflow-y-auto"
          aria-multiselectable="true"
          aria-label="Assigned to filter options"
        >
          {filteredUsers.map((user) => (
            <button
              key={user._id}
              className={`w-full flex items-center px-3 py-2 rounded-md text-sm mb-1 transition-colors text-left ${
                selectedUserIds.includes(user._id)
                  ? "bg-accent text-accent-foreground font-medium"
                  : "hover:bg-accent/50"
              }`}
              onClick={() => handleAssignedToSelect(user._id)}
            >
              <span>
                {user.full_name}
                {currentUserId === user._id && " (you)"}
              </span>
            </button>
          ))}
          {filteredUsers.length === 0 && (
            <p className="text-sm text-muted-foreground px-3 py-2">
              No users found
            </p>
          )}
        </div>
      </div>

      <div className="p-4 border-t border-border">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Notification centre
        </h3>
        {COMPUTED_NOTIFICATIONS.map((notification) => (
          <NotificationItem
            key={notification.key}
            label={notification.label}
            count={facetCounts?.notifications[notification.key] ?? 0}
            isActive={
              filters.notifications?.includes(notification.filterValue) ?? false
            }
            onClick={() =>
              handleNotificationFilterClick(notification.filterValue)
            }
          />
        ))}
        {!isInternal && (
          <NotificationItem
            key={NEW_MESSAGES_NOTIFICATION.key}
            label={NEW_MESSAGES_NOTIFICATION.label}
            count={
              facetCounts?.notifications_inc_rejected[
                NEW_MESSAGES_NOTIFICATION.key
              ] ?? 0
            }
            isActive={
              filters.notifications?.includes(
                NEW_MESSAGES_NOTIFICATION.filterValue
              ) ?? false
            }
            onClick={() =>
              handleNotificationFilterClick(
                NEW_MESSAGES_NOTIFICATION.filterValue
              )
            }
          />
        )}
        {!isInternal &&
          STANDARD_NOTIFICATIONS.map((notification) => (
            <NotificationItem
              key={notification.key}
              label={notification.label}
              count={facetCounts?.notifications[notification.key] ?? 0}
              isActive={
                filters.notifications?.includes(notification.filterValue) ??
                false
              }
              onClick={() =>
                handleNotificationFilterClick(notification.filterValue)
              }
            />
          ))}
        {!isInternal && hasBookingCustomFieldLabel && (
          <NotificationItem
            key={CUSTOMER_DETAILS_NOTIFICATION.key}
            label={CUSTOMER_DETAILS_NOTIFICATION.label}
            count={
              facetCounts?.notifications[CUSTOMER_DETAILS_NOTIFICATION.key] ?? 0
            }
            isActive={
              filters.notifications?.includes(
                CUSTOMER_DETAILS_NOTIFICATION.filterValue
              ) ?? false
            }
            onClick={() =>
              handleNotificationFilterClick(
                CUSTOMER_DETAILS_NOTIFICATION.filterValue
              )
            }
          />
        )}
        {!isInternal && (
          <NotificationItem
            key={CUSTOMER_CANCELLED_NOTIFICATION.key}
            label={CUSTOMER_CANCELLED_NOTIFICATION.label}
            count={
              facetCounts?.notifications_inc_rejected[
                CUSTOMER_CANCELLED_NOTIFICATION.key
              ] ?? 0
            }
            isActive={
              filters.notifications?.includes(
                CUSTOMER_CANCELLED_NOTIFICATION.filterValue
              ) ?? false
            }
            onClick={() =>
              handleNotificationFilterClick(
                CUSTOMER_CANCELLED_NOTIFICATION.filterValue
              )
            }
          />
        )}
      </div>
    </div>
  );
};
